import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, CheckCircle2, Clock, XCircle, Phone, Calendar, Hotel, Hash } from "lucide-react";
import PageTransition from "../components/PageTransition";
import api from "../lib/api";
import { useApp } from "../context/AppContext";

const Track = () => {
    const { settings, formatPrice } = useApp();
    const [code, setCode] = useState("");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setResult(null);
        try {
            const { data } = await api.get(`/reservations/lookup?code=${encodeURIComponent(code)}&phone=${encodeURIComponent(phone)}`);
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.detail || "Bulunamadı");
        } finally {
            setLoading(false);
        }
    };

    const statusMap = {
        pending: { label: "Beklemede", icon: Clock, color: "text-yellow-400 border-yellow-400/40", desc: "Talebiniz alındı. Ekibimiz en kısa sürede WhatsApp üzerinden sizinle iletişime geçecek." },
        approved: { label: "Onaylandı", icon: CheckCircle2, color: "text-emerald-400 border-emerald-400/40", desc: "Rezervasyonunuz onaylandı! Mübarek yolculuğunuz için tüm hazırlıklar tamamdır." },
        rejected: { label: "Reddedildi", icon: XCircle, color: "text-red-400 border-red-400/40", desc: "Maalesef rezervasyonunuz tamamlanamadı. Detaylar için bizimle iletişime geçin." },
    };

    return (
        <PageTransition>
            <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <div className="ornament-divider mb-6"><Search className="w-4 h-4 text-gold" /></div>
                    <h1 className="font-serif text-5xl text-center mb-3">Rezervasyon Sorgula</h1>
                    <p className="text-muted_green text-center mb-12">
                        Rezervasyon numaranız ve telefon numaranız ile durumunuzu öğrenin
                    </p>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="border border-border_green bg-emerald_surface p-8 space-y-5"
                >
                    <div>
                        <label className="label-dark"><Hash className="w-3 h-3 inline mr-1" /> Rezervasyon Numarası</label>
                        <input
                            type="text"
                            required
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="ASS-XXXXXX"
                            className="input-dark text-center text-xl tracking-[0.3em] font-mono"
                            data-testid="track-code-input"
                        />
                    </div>
                    <div>
                        <label className="label-dark"><Phone className="w-3 h-3 inline mr-1" /> Telefon Numarası</label>
                        <input
                            type="tel"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+90 5xx xxx xx xx"
                            className="input-dark"
                            data-testid="track-phone-input"
                        />
                    </div>
                    <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="track-submit-btn">
                        {loading ? "Aranıyor..." : "Sorgula"}
                    </button>
                </motion.form>

                {error && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-6 border border-red-500/40 bg-red-500/5 text-red-400 p-5 text-center text-sm"
                        data-testid="track-error"
                    >
                        {error}
                    </motion.div>
                )}

                {result && (() => {
                    const s = statusMap[result.status] || statusMap.pending;
                    const Icon = s.icon;
                    return (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-8 border border-gold bg-emerald_surface"
                            data-testid="track-result"
                        >
                            <div className="p-8 text-center border-b border-border_green">
                                <Icon className={`w-12 h-12 mx-auto mb-4 ${s.color.split(" ")[0]}`} />
                                <div className={`inline-block text-xs uppercase tracking-[0.25em] px-3 py-1.5 border mb-4 ${s.color}`}>
                                    {s.label}
                                </div>
                                <h2 className="font-serif text-3xl mb-2">{result.hotel_name}</h2>
                                <p className="text-sm text-muted_green max-w-md mx-auto">{s.desc}</p>
                            </div>
                            <div className="p-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                                <div><span className="text-muted_green flex items-center gap-1"><Hash className="w-3 h-3" /> Kod</span><div className="font-mono text-gold mt-1">{result.reservation_code}</div></div>
                                <div><span className="text-muted_green flex items-center gap-1"><Hotel className="w-3 h-3" /> Oda</span><div className="mt-1">{result.room_name} × {result.rooms_count}</div></div>
                                <div><span className="text-muted_green flex items-center gap-1"><Calendar className="w-3 h-3" /> Giriş</span><div className="mt-1">{result.check_in}</div></div>
                                <div><span className="text-muted_green flex items-center gap-1"><Calendar className="w-3 h-3" /> Çıkış</span><div className="mt-1">{result.check_out}</div></div>
                                <div><span className="text-muted_green">Gece / Misafir</span><div className="mt-1">{result.nights} gece · {result.guests} kişi</div></div>
                                <div><span className="text-muted_green">Toplam</span><div className="text-gold font-serif text-lg mt-1">{formatPrice(result.total_price)}</div></div>
                            </div>
                            <div className="px-6 pb-6 border-t border-border_green pt-4 text-xs text-muted_green">
                                Sorularınız için WhatsApp: <span className="text-gold">+{settings.whatsapp_number}</span>
                            </div>
                        </motion.div>
                    );
                })()}
            </div>
        </PageTransition>
    );
};

export default Track;
